<template>
  <div class="unit container pt-0">
    <div class="background-img-container">
      <div class="background-image"></div>
    </div>
    <div
      class="inactive-user"
      v-if="this.$route.name !== 'Login'"
      @mousemove="updateActivity"
      @keypress="updateActivity"
      @touchstart.passive="updateActivity"
      @touchmove.passive="updateActivity"
    >
      <div class="contentSection" :class="{ 'pl-3': isXsOrSm }">
        <div v-if="unit">
          <div
            v-for="u in unit"
            :key="u.id"
            class="unit-container"
            :class="{ 'hide-title': isVisible }"
            :style="{ top: '-' + position + 'px' }"
          >
            <div
              class="unit-meta-container"
              :class="{ 'top-38': hasNotifications.length }"
            >
              <div class="mb-0 mt-4">
                <h2
                  class="base-year"
                  :class="currentCourse[0].baseYear"
                  v-html="u.title"
                ></h2>
              </div>
              <div class="mb-7 mt-0">
                <span class="font-weight-bold" v-html="u.subtitle"></span>
              </div>
            </div>

            <UserEditUnit
              :unit="u"
              :unitbackdoor="unitbackdoor"
              :activecourse="currentCourse"
              :hasNotifications="hasNotifications"
              @changeV="changeVisibility($event)"
              @position="changePosition($event)"
              @baseYearChanged="handleBaseYearChanged"
            />

            <UnitNavigation
              :hasNotifications="hasNotifications"
              :unit="u"
              :activecourse="currentCourse"
            />

            <div
              class="scroll-buttons"
              v-if="
                !isMobile() &&
                !notesOpen &&
                !highlightsOpen &&
                !commentsOpen &&
                ((getRoles && getRoles.includes('tester')) ||
                  (getFeatures && getFeatures.includes('page-scroll')))
              "
            >
              <v-btn
                icon
                class="scroll-up"
                id=""
                variant="text"
                @mouseup.stop="scroll('up', 'end')"
                @mousedown.stop="scroll('up', 'start')"
              >
                <v-icon>arrowup</v-icon>
              </v-btn>
              <v-btn
                icon
                class="scroll-down"
                id=""
                variant="text"
                @mouseup.stop="scroll('down', 'end')"
                @mousedown.stop="scroll('down', 'start')"
              >
                <v-icon>arrowdown</v-icon>
              </v-btn>
            </div>
          </div>
        </div>
        <div v-else>{{ $t('unitStudy.noUnit') }}</div>
      </div>
    </div>
  </div>
</template>

<script>
  import db from '@/firebase/init'
  import { scopeToChurch } from '@/helpers/churchScope'
  import firebase from 'firebase'
  import axios from 'axios'
  import { mapState } from 'vuex'
  import { mapGetters } from 'vuex'
  import UserEditUnit from '@/components/utilities/UserEditUnit'
  import UnitNavigation from '@/components/utilities/UnitNavigation'

  export default {
    name: 'Unit',
    components: {
      UserEditUnit,
      UnitNavigation,
    },
    data() {
      return {
        feedback: null,
        unit: [],
        currentCourse: [],
        unitbackdoor: 0,
        isVisible: false,
        position: '',
        hasNotifications: [],
        intervalId: undefined,
        // unitNotes: false,
        lastActivityTime: new Date(),
        inactivityThreshold: 180 * 60 * 1000, // 180 minutes (3 hours) in milliseconds
      }
    },
    watch: {
      '$route.params': {
        handler(params) {
          if (params && params.id) {
            this.init()
          }
          this.$store.dispatch('AppData/highlightsOpen', false)
        },
        deep: true,
        immediate: true,
      },
      getActiveInternalNotifications(newVal) {
        if (!Array.isArray(newVal) || newVal.length === 0) {
          this.hasNotifications = []
          return
        }

        const userRoles = this.$store.getters['UserData/getRoles']
        const isSupperAdmin = this.checkAuthorization(['superAdmin'])

        if (isSupperAdmin) {
          this.hasNotifications = []
          return
        }

        // Use flat() instead of reduce for better performance
        const flattened = newVal.flat()

        if (flattened.length === 0) {
          this.hasNotifications = []
          return
        }

        // Create a Set for faster role checking
        const userRolesSet = new Set(userRoles)

        const hasAudience = flattened.filter((el) => {
          // First check to make sure it is NOT a notify
          if (el.messagetype === 'notify') return false

          // Use Set.has() for O(1) lookup instead of Array.some()
          return el.audience && el.audience.some((r) => userRolesSet.has(r))
        })

        // Optimize sorting - use more efficient comparison
        hasAudience.sort((a, b) => {
          const aHasAllChurches = a.audience?.includes('allchurches') || false
          const bHasAllChurches = b.audience?.includes('allchurches') || false

          if (aHasAllChurches === bHasAllChurches) {
            return (b.created || 0) - (a.created || 0)
          }
          return bHasAllChurches - aHasAllChurches
        })

        this.hasNotifications = hasAudience
      },
    },
    computed: {
      ...mapState('UserData', ['user']),
      ...mapState('AppData', ['userActiveCourse']),
      ...mapGetters('AppData', [
        'getCourse',
        'getActiveCourse',
        'getActiveInternalNotifications',
        'getFeatures',
      ]),
      ...mapGetters('UserData', ['getRoles']),
      isXsOrSm() {
        const d = this.$vuetify.display
        // matches your old "xsOnly || smOnly"
        return d.xs || d.sm
        // or simply: return d.smAndDown
      },
      notesOpen: {
        get() {
          return this.$store.getters['AppData/notesOpen']
        },
        set(value) {
          this.$store.dispatch('AppData/notesOpen', value)
        },
      },
      highlightsOpen: {
        get() {
          return this.$store.getters['AppData/highlightsOpen']
        },
        set(value) {
          this.$store.dispatch('AppData/highlightsOpen', value)
        },
      },
      commentsOpen: {
        get() {
          return this.$store.getters['AppData/commentsOpen']
        },
        set(value) {
          this.$store.dispatch('AppData/commentsOpen', value)
        },
      },
    },
    methods: {
      handleBaseYearChanged(baseYear) {
        // console.log('Unit received baseYearChanged event with value:', baseYear); // Debug log
        this.$emit('baseYearChanged', baseYear)
      },
      setActivityCheckInterval() {
        // console.log('1 ', new Date())
        // Check every minute for inactivity
        this.activityCheckInterval = setInterval(() => {
          // console.log('check every 60 sec')
          const currentTime = new Date()
          // console.log('2 ', currentTime - this.lastActivityTime, this.inactivityThreshold)
          if (currentTime - this.lastActivityTime > this.inactivityThreshold) {
            this.logout()
          }
        }, 60 * 1000) // Check every 60 seconds
      },
      updateActivity() {
        // Update lastActivityTime on any user activity
        this.lastActivityTime = new Date()
        // console.log('updateActivity ', this.lastActivityTime)
      },
      logout() {
        // console.log('logged out @ ', new Date())
        this.$store.dispatch('UserData/userSignOut')

        this.$store.dispatch('reset')
      },
      changeVisibility(isVisible) {
        this.isVisible = isVisible
      },
      changePosition(position) {
        this.position = position
      },
      getLocalizedUnit(unit, requiredLanguage) {
        if (!unit) return unit
        const rawLang = (requiredLanguage || 'en').toLowerCase()
        // Normalize language keys to the shapes used in localizedContent.
        const lang = rawLang.startsWith('pt') ? 'pt' : 'en'
        const lc =
          (unit.localizedContent && unit.localizedContent[lang]) ||
          (lang === 'pt' && unit.pt ? unit.pt : null)
        const merged = { ...unit }
        const textFields = [
          'title',
          'subtitle',
          'description',
          'listenLabel',
          'completeLabel',
          'continueLearningLabel',
          'listen',
          'complete',
          'continueLearning',
          'truthBodyContent',
          'truthInBible',
          'equipBodyContent',
          'accountBodyContent',
          'footnotesBodyContent',
          'copyrightBodyContent',
        ]

        if (lc) {
          textFields.forEach((field) => {
            if (typeof lc[field] === 'string' && lc[field].trim() !== '') {
              merged[field] = lc[field]
            }
          })
        }

        const arrayFields = [
          'truthVersesQuotes',
          'truthStudyQuestions',
          'equipStudyQuestions',
          'accountStudyQuestions',
        ]

        if (lc) {
          arrayFields.forEach((field) => {
            if (Array.isArray(lc[field]) && lc[field].length > 0) {
              merged[field] = lc[field]
            }
          })
        }

        // For assignments:
        // - Non-English stays strict to localized content.
        // - English can fall back to legacy top-level fields for older units.
        const assignmentFields = [
          'listenLabel',
          'completeLabel',
          'continueLearningLabel',
          'listen',
          'complete',
          'continueLearning',
        ]
        assignmentFields.forEach((field) => {
          if (lc && field in lc) {
            merged[field] = lc[field] || ''
          } else if (lang === 'en' && typeof unit[field] === 'string') {
            merged[field] = unit[field]
          } else {
            merged[field] = ''
          }
        })

        return merged
      },
      getLocalizedWeekContent(week, requiredLanguage) {
        if (!week) return week
        const lang = requiredLanguage || 'en'
        const lc =
          (week.localizedContent && week.localizedContent[lang]) ||
          (lang === 'pt' && week.pt ? week.pt : null)
        if (!lc) return week

        const merged = { ...week }
        const textFields = [
          'missionBodyContent',
          'supplicationBodyContent',
        ]
        textFields.forEach((field) => {
          if (typeof lc[field] === 'string' && lc[field].trim() !== '') {
            merged[field] = lc[field]
          }
        })

        const arrayFields = [
          'missionStudyQuestions',
          'supplicationStudyQuestions',
          'supplicationVerseQuotes',
        ]
        arrayFields.forEach((field) => {
          if (Array.isArray(lc[field]) && lc[field].length > 0) {
            merged[field] = lc[field]
          }
        })

        return merged
      },
      async init() {
        this.unit = []
        this.currentCourse = []
        this.unitbackdoor++

        let unitID = this.$route.params.id
        let courseID = this.$route.params.courseID
        let sectionID = this.$route.params.sectionID

        let currentCourseRef = db.collection('currentcourses')
        currentCourseRef = currentCourseRef.where('storageID', '==', courseID)
        currentCourseRef = scopeToChurch(currentCourseRef, this.$store)
        let activeRef = await currentCourseRef.get()

        if (!activeRef.empty) {
          // let source = activeRef.metadata.fromCache ? "local cache" : "server"

          const doc = activeRef.docs[0]
          let course = doc.data()
          course.id = doc.id

          if (!activeRef.metadata.fromCache) {
            // console.log("unit Data came from " + source)
            // if we are connected to the server
            const storage = firebase.storage()
            const storageRef = storage.ref()
            const objectRef = storageRef.child(course.storageRef)
            let url = await objectRef.getDownloadURL()
            let resp = await axios.get(url)

            const missionRef = firebase.storage().ref().child(course.missions)
            let missionURL = await missionRef.getDownloadURL()
            let missionResp = await axios.get(missionURL)

            resp.data.missions = missionResp.data

            this.currentCourse.push(resp.data)
          } else {
            // we are NOT connected to the server. look in the store
            // clone the vues object to avoice mutation errors
            const clone = JSON.parse(JSON.stringify(this.getActiveCourse[0]))
            // add the cloned object from the store
            this.currentCourse.push(clone)
          }

          // add the storageRef to the currentCourse array
          this.currentCourse[0].storageRef = course.storageRef

          const currentSection = this.currentCourse[0].sections.filter(
            (item) => item.id === sectionID
          )
          // const currentSection = this.userActiveCourse[0].sections.filter(item => item.id === sectionID)
          const currentUnit = currentSection[0].units.filter(
            (u) => u.id === unitID
          )
          const requiredLanguage =
            currentSection[0]?.requiredLanguage ||
            this.currentCourse[0]?.requiredLanguage ||
            'en'

          // get the current mission and supplication
          if (this.currentCourse[0].missions) {
            // if(this.userActiveCourse[0].missions) {
            const currentWeek = this.currentCourse[0].missions.filter(
              (item) => item.unitID == currentUnit[0].id
            )
            // const currentWeek = this.userActiveCourse[0].missions.filter(item => item.unitID == currentUnit[0].id)

            let weekSource = currentWeek && currentWeek[0] ? currentWeek[0] : null
            if (weekSource && requiredLanguage !== 'en' && weekSource.id) {
              try {
                const weekSnap = await db
                  .collection('weeks')
                  .doc(weekSource.id)
                  .get()
                if (weekSnap.exists) {
                  weekSource = { ...weekSource, ...weekSnap.data(), id: weekSnap.id }
                }
              } catch (e) {
                // fallback to course-provided week data
              }
            }

            const localizedWeek = weekSource
              ? this.getLocalizedWeekContent(weekSource, requiredLanguage)
              : null

            // add mission and supplication to the unit
            currentUnit[0].missionBodyContent =
              localizedWeek?.missionBodyContent ?? weekSource?.missionBodyContent
            currentUnit[0].missionStudyQuestions =
              localizedWeek?.missionStudyQuestions ??
              weekSource?.missionStudyQuestions
            currentUnit[0].supplicationBodyContent =
              localizedWeek?.supplicationBodyContent ??
              weekSource?.supplicationBodyContent
            currentUnit[0].supplicationStudyQuestions =
              localizedWeek?.supplicationStudyQuestions ??
              weekSource?.supplicationStudyQuestions
            // currentUnit[0].supplicationreference = currentWeek[0].supplicationreference
            // currentUnit[0].supplicationverse = currentWeek[0].supplicationverse
            currentUnit[0].supplicationVerseQuotes =
              localizedWeek?.supplicationVerseQuotes ??
              weekSource?.supplicationVerseQuotes
          }

          let currentCourseContentRef = db.collection('coursecontent')
          currentCourseContentRef = currentCourseContentRef.where(
            'courseID',
            '==',
            courseID
          )
          currentCourseContentRef = currentCourseContentRef.where(
            'sectionID',
            '==',
            sectionID
          )
          currentCourseContentRef = currentCourseContentRef.where(
            'unitID',
            '==',
            unitID
          )
          currentCourseContentRef = currentCourseContentRef.orderBy('unitNotes')
          let activeCourseContentRef = await currentCourseContentRef.get()
          // console.log('activeCourseContentRef ', activeCourseContentRef)
          if (!activeCourseContentRef.empty) {
            const notesdoc = activeCourseContentRef.docs[0]
            let note = notesdoc.data()
            note.id = notesdoc.id

            // console.log('note ', note)
            currentUnit[0].unitNotes = note.unitNotes
          }
          currentUnit[0] = this.getLocalizedUnit(currentUnit[0], requiredLanguage)
          this.unit.push(currentUnit[0])
          // console.log('this.currentCourse ', this.currentCourse)
        }
      },
      async getActiveNotifications() {
        this.$store.dispatch('AppData/resetActiveInternaleNotifications', [], {
          root: true,
        })
        this.$store.dispatch(
          'AppData/getActiveInternalNotifications',
          {
            cid: this.$store.getters['AppData/cid'],
            user: this.user,
          },
          { root: true }
        )
      },
      checkAuthorization(permissions) {
        const userRoles = this.$store.getters['UserData/getRoles']
        if (permissions && userRoles) {
          const hasRole = permissions.filter(() =>
            userRoles.some((role) => permissions.includes(role))
          )
          if (hasRole && hasRole.length > 0) return true
        }
        return false
      },
      scroll(dir, event) {
        var scrollAmount = 100

        if (dir == 'down' && event == 'start') {
          this.intervalId = setInterval(() => {
            // scrollAmount = ( parseInt( scrollAmount == null ? 0 : scrollAmount ) + 100 )
            document.getElementById('userEditUnitContainer').scrollTop +=
              scrollAmount
          }, 100)
        }
        if (dir == 'down' && event == 'end') {
          clearInterval(this.intervalId)
        }

        if (dir == 'up' && event == 'start') {
          this.intervalId = setInterval(() => {
            // scrollAmount = ( parseInt( scrollAmount == null ? 0 : scrollAmount ) + 100 )
            document.getElementById('userEditUnitContainer').scrollTop -=
              scrollAmount
          }, 100)
        }
        if (dir == 'up' && event == 'end') {
          clearInterval(this.intervalId)
        }
      },
      isMobile() {
        return this.$vuetify.display.mobile
      },
    },
    created() {
      this.getActiveNotifications()
    },
    mounted() {
      this.setActivityCheckInterval()
    },
    beforeUnmount() {
      clearInterval(this.activityCheckInterval)
    },
  }
</script>

<style scoped>
  [v-cloak] {
    display: none;
  }
  .unit.container .background-img-container {
    position: fixed;
    /* z-index: -1; */
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    overflow: hidden;
  }
  .unit.container .background-image {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: url(../../assets/mountains.jpg) no-repeat 50% 0;
    background-size: cover;
    -webkit-transform: translate3d(0, 0, 0);
    transform: translate3d(0, 0, 0);
    filter: grayscale(100%) blur(0px);
  }
  .v-theme--dark .unit.container .background-image {
    filter: grayscale(100%) blur(0px) invert(1);
  }
  .background-image:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(255, 255, 255, 0.9);
  }
  .unit .contentSection {
    /* margin-bottom: 50%; */
    max-height: 90vh;
    overflow: hidden;
    position: relative;
    max-width: 640px;
    margin: auto;
  }
  .unit-meta-container.top-38 {
    padding-top: 38px;
  }
  /* .unit .contentSection > div {
    margin-bottom: 50%;
} */
  /* .unit .the-journey-logo {
    margin: 60px 0;
} */
  /* .theme--dark .unit .the-journey-logo svg path:not(.a) {
    fill: #ffffff;
} */
  /* .unit .v-alert {
    margin: -1px auto;
} */

  /* .unit .journey-start-date,
.unit .app-guide-title {
    text-transform: uppercase;
    font-weight: 700;
    text-align: center;
    font-size: 20px;
}
.unit .journey-start-date {
    color: #a5d867;
}
.unit .app-guide-icon-title {
    font-size: 12px;
    text-transform: uppercase;
    font-weight: 700;
}
.unit #app-guide-icon-grn {
    cursor: pointer;
} */
  .scroll-buttons {
    position: fixed;
    display: flex;
    flex-direction: column;
    top: 90px;
    right: 20%;
  }
  @media only screen and (min-width: 982px) {
    .unit .contentSection {
      max-width: 800px;
    }
  }
  @media only screen and (max-width: 767px) {
    .unit .contentSection {
      padding-top: 56px;
    }
    .unit-meta-container {
      transition: 0.4s ease;
    }
    .hide-title .unit-meta-container {
      opacity: 0;
    }
    .unit-container {
      position: relative;
    }
  }
</style>
